import React, { ReactNode } from "react";

import BreadCrumb from "shared/BreadCrumb";
import { AppLayout } from "shared/Layout";
import { fetchSettings } from "shared/SettingsProvider/settings.helper";
import { BestSellersCatalog } from "shared/catalog/BestSellersCatalog";
import { BestSellersContextProvider } from "shared/catalog/BestSellersCatalog/BestSellersContext";
import { getBestSellerEntries } from "shared/catalog/BestSellersCatalog/best-sellers-catalog.helper";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { fetchSEO, withSeo } from "utils/with-seo.utils";

import { Banner } from "../Banner";
import { ImgDirection, TextBlockWithImg } from "../TextBlockWithImg";

import { Labelize } from "./Labelize";

export type CommentCaMarcheProps = Awaited<
  ReturnType<typeof getStaticProps>
>["props"];

export const CommentCaMarche = ({
  bestSellerEntries,
}: CommentCaMarcheProps) => {
  const i18n = useI18n();

  return (
    <>
      <div className="mx-auto max-w-[1440px]">
        <BreadCrumb
          parts={[
            { label: i18n.t("breadcrumb.home"), to: "/" },
            {
              label: i18n.t("breadcrumb.howItWorks"),
              to: "/comment-ca-marche",
            },
          ]}
        />
        <div className="px-5 xl:px-32">
          <Banner
            firstLine={i18n.t("howItWorks.title.firstPart")}
            secondLine={i18n.t("howItWorks.title.secondPart")}
          />
          <div className="flex flex-col gap-8 mb-16 lg:gap-12">
            <TextBlockWithImg
              title={i18n.t("howItWorks.firstTitle", {}, { html: true })}
              description={i18n.t(
                "howItWorks.firstParagraph",
                {},
                { html: true },
              )}
              imgSrc="/images/seo/comment-ca-marche/firstPic.webp"
              imgDirection={ImgDirection.Right}
              priority={true}
            />
            <TextBlockWithImg
              title={i18n.t("howItWorks.secondTitle", {}, { html: true })}
              description={i18n.t(
                "howItWorks.secondParagraph",
                {},
                { html: true },
              )}
              imgSrc="/images/seo/comment-ca-marche/secondPic.webp"
              imgDirection={ImgDirection.Left}
            />
            <TextBlockWithImg
              title={i18n.t("howItWorks.thirdTitle", {}, { html: true })}
              description={i18n.t(
                "howItWorks.thirdParagraph",
                {},
                { html: true },
              )}
              imgSrc="/images/seo/banner.webp"
              imgDirection={ImgDirection.Right}
            />
            <TextBlockWithImg
              title={i18n.t("howItWorks.fourthTitle", {}, { html: true })}
              description={i18n.t(
                "howItWorks.fourthParagraph",
                {},
                { html: true },
              )}
              imgSrc="/images/seo/comment-ca-marche/fourthPic.webp"
              imgDirection={ImgDirection.Left}
            />
            <TextBlockWithImg
              title={i18n.t("howItWorks.fifthTitle", {}, { html: true })}
              description={i18n.t(
                "howItWorks.fifthParagraph",
                {},
                { html: true },
              )}
              imgSrc="/images/seo/comment-ca-marche/fifthPic.webp"
              imgDirection={ImgDirection.Right}
            />
          </div>
          <Labelize />
        </div>
      </div>
      <BestSellersContextProvider entries={bestSellerEntries}>
        <div className="flex flex-col items-center py-12">
          <h2 className="text-4xl font-medium text-center lg:text-6xl">
            {i18n.t("howItWorks.bestseller.title")}
          </h2>
          <div className="flex flex-wrap gap-4 justify-center px-5 my-8 w-full max-w-6xl lg:my-12">
            <BestSellersCatalog />
          </div>
        </div>
      </BestSellersContextProvider>
    </>
  );
};

CommentCaMarche.getLayout = (
  page: ReactNode,
  pageProps: CommentCaMarcheProps,
) => (
  <AppLayout withBanner {...pageProps}>
    {page}
  </AppLayout>
);

export const getStaticProps = async () => {
  const translations = await fetchTranslations([
    "marquee",
    "how-it-works",
    "breadcrumb",
    "catalog",
  ]);

  const seo = await fetchSEO("comment-ca-marche");

  return {
    props: {
      translations,
      seo,
      settings: await fetchSettings(),
      bestSellerEntries: await getBestSellerEntries(),
    },
  };
};

export default withApp(CommentCaMarche, [withSeo]);
